import { useEffect, useMemo, useState } from 'react';
import { AchievementType, ProfileContextStateModel } from '~/apps/smartsend/lib/context/ProfileContext';
import Profile from './screens/profile';
import AccountsScreen from './screens/accounts';
import RulesScreen from './screens/rules';
import { Customer } from '~/lib/api/smartsend/types';
import IntroScreen from './screens/intro';
import CompleteScreen from './screens/complete';
import SubscribeScreen from './screens/subscribe';
import SubscribeSuccessScreen from './screens/subscribe/subscribe-success';
import { OnboardingScreenWrapper } from './OnboardingScreenWrapper';
import { onlyUnique } from '~/lib/utils/array';
import { SetupProgress } from './SetupProgress';
import { clearPendingSubscriptionPlan } from '~/hooks/use-pending-subscription-plan';
import { usePendingPlanDecision } from '~/apps/subscriptions/use-pending-plan-decision';
import { SubscribeOutcome } from '~/apps/subscriptions/subscribe-panel';

type props = {
  isXs: boolean;
  loading: boolean;
  sendNow?: () => void;
  data: ProfileContextStateModel;
  justLinked?: string;
  justFailed?: string;

  markAsDone?: (
    keys: AchievementType[],
    outstandingActionsToAdd: string[],
    outstandingActionsToRemove: string[],
    meta: { [key: string]: any }
  ) => void;
  markAsTodo: (actions: string[]) => void;
};

const stages = ['WELCOME', 'CREATEACCOUNT', 'SETDEFAULTS', 'CONNECTSTORE', 'SENDPARCEL'];

const SmartSendSetup = ({ justFailed, justLinked, isXs, loading, data, markAsDone, sendNow, markAsTodo }: props) => {
  const [startedSetup, setSetupStarted] = useState(false);
  const [justFailedAcknowledged, setJustFailedAcknowledged] = useState(false);
  const [justLinkedAcknowledged, setJustLinkedAcknowledged] = useState(false);

  const done = useMemo(
    () => (data?.achievements.filter((x) => x.value).map((x) => x.key) ?? []).filter(onlyUnique),
    [data]
  );

  const [customer, setCustomer] = useState<Customer | null>();
  // Answers both "should this step appear" and, at the end of onboarding, "where do they go next" -
  // see use-pending-plan-decision.ts for why a subscriber never sees it.
  const planDecision = usePendingPlanDecision();
  // Set the moment SubscribeScreen reports an actual subscribe (never a skip) - holds the achievement
  // back so the confirmation card gets a turn before `done` moves the flow on. Cleared once the card's
  // own timer calls back through, at which point the achievement is finally marked.
  const [subscribedOutcome, setSubscribedOutcome] = useState<SubscribeOutcome | null>(null);

  const screenToShow = useMemo(() => {
    if (subscribedOutcome) {
      return 'SUBSCRIBE_SUCCESS';
    }

    // A pending tier takes priority over everything else - a customer who arrived to subscribe to a
    // specific plan sees that before any other onboarding step, but only once, and never if they
    // already hold a plan. While entitlements are still settling we cannot know which, so the flow
    // falls through to INTRO rather than showing a step that might have to be taken away again.
    if (planDecision.kind === 'show' && !done.includes('SmartSendSetupSubscriptionScreen')) {
      return 'SUBSCRIBE';
    }

    // If they just linked an account, go to complete screen
    if (justLinked && !justLinkedAcknowledged) {
      return 'COMPLETE';
      // There is a component for acknowledging this but we've combined that into the complete page for simplicity.
      // return 'ACCOUNTS';
    }

    // Failed to link an account
    if (justFailed && !justFailedAcknowledged) {
      return 'ACCOUNTS';
    }

    if ((loading || !startedSetup) && !justLinked && !justFailed) {
      return 'INTRO';
    }

    // Only show if they haven't added a profile yet
    if (!done.includes('SmartSendActionCollectionAddressAdded')) {
      return 'PROFILE';
    }

    // Only show if they haven't seen the rule screen yet, or if they have just linked an account
    if (!done.includes('SmartSendSetupRuleScreen')) {
      return 'RULES';
    }

    // This one is specifically the screen as they can add more if they want.
    if (!done.includes('SmartSendSetupAccountScreen')) {
      return 'ACCOUNTS';
    }

    return 'COMPLETE';
  }, [
    startedSetup,
    justLinked,
    done,
    loading,
    justFailed,
    justFailedAcknowledged,
    justLinkedAcknowledged,
    planDecision,
    subscribedOutcome
  ]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [screenToShow]);

  const Progress = useMemo(() => {
    const cs = (() => {
      switch (screenToShow) {
        case 'INTRO':
        case 'PROFILE':
          return 'CREATEACCOUNT';
        case 'RULES':
          return 'SETDEFAULTS';
        case 'ACCOUNTS':
          return 'CONNECTSTORE';
        case 'COMPLETE':
          return 'SENDPARCEL';
        default:
          return 'WELCOME';
      }
    })();

    return <SetupProgress isXs={isXs} stages={stages} currentStage={cs} />;
  }, [isXs, screenToShow]);

  return (
    <OnboardingScreenWrapper progress={Progress}>
      {screenToShow === 'SUBSCRIBE' && planDecision.kind === 'show' && (
        <SubscribeScreen
          isXs={isXs}
          pendingPlan={planDecision.plan}
          onDone={(outcome) => {
            // An actual subscribe gets a confirmation card first - the achievement is marked once
            // that card's own timer calls back through, below. A skip (no outcome, or one naming no
            // tier) has nothing to confirm, so it marks done immediately, as before.
            if (outcome?.subscriptionId) {
              setSubscribedOutcome(outcome);
              return;
            }
            clearPendingSubscriptionPlan();
            markAsDone(['SmartSendSetupSubscriptionScreen'], [], [], outcome ?? { tier: null, interval: null });
          }}
        />
      )}

      {screenToShow === 'SUBSCRIBE_SUCCESS' && subscribedOutcome && (
        <SubscribeSuccessScreen
          outcome={subscribedOutcome}
          onDone={() => {
            clearPendingSubscriptionPlan();
            markAsDone(['SmartSendSetupSubscriptionScreen'], [], [], subscribedOutcome);
            setSubscribedOutcome(null);
          }}
        />
      )}

      {screenToShow === 'INTRO' && <IntroScreen isXs={isXs} onDone={() => setSetupStarted(true)} />}
      {screenToShow === 'PROFILE' && (
        <Profile
          isXs={isXs}
          onDone={(c) => {
            markAsDone(['SmartSendSetupCollectionAddressScreen', 'SmartSendActionCollectionAddressAdded'], [], [], {});
            setCustomer(c);
          }}
        />
      )}
      {screenToShow === 'RULES' && (
        <RulesScreen
          numberOfParcels={data.numberOfParcelsPerWeek}
          key={customer?.Id}
          customer={customer}
          isXs={isXs}
          onSkip={() => {
            markAsDone(['SmartSendSetupRuleScreen'], [], [], {});
          }}
          onDone={(added, meta) => {
            if (added > 0) {
              markAsDone(['SmartSendSetupRuleScreen', 'SmartSendActionRuleAdded'], [], [], meta);
            } else {
              markAsDone(['SmartSendSetupRuleScreen'], [], [], meta);
            }
          }}
        />
      )}
      {screenToShow === 'ACCOUNTS' && (
        <AccountsScreen
          isXs={isXs}
          justLinked={justLinked}
          justFailed={justFailed}
          signupIntegrations={data.signupIntegrations}
          onDone={(outstanding, done) => {
            // Record Integrations
            const achievements: AchievementType[] = ['SmartSendSetupAccountScreen'];
            if (!done?.length) {
              // Only onboarding account screen
            } else if (done?.some((x) => x === 'csv')) {
              achievements.push('SmartSendActionCsvUploaded');
            } else {
              achievements.push('SmartSendActionAccountLinked');
            }
            markAsDone(achievements, outstanding, done, {});
          }}
          onTodo={markAsTodo}
          clearJustFailed={() => setJustFailedAcknowledged(true)}
          clearJustLinked={() => setJustLinkedAcknowledged(true)}
        />
      )}
      {screenToShow === 'COMPLETE' && (
        <CompleteScreen
          isXs={isXs}
          actions={data.outstandingActions}
          done={done}
          justLinked={justLinked}
          onDone={sendNow}
        />
      )}
    </OnboardingScreenWrapper>
  );
};

export default SmartSendSetup;
